<template>
  <div :class="clsx('possibilities-section', { 'animations-disabled': !animationEnabled })" id="possibilities">
    <IntersectionObserver
      :animation-enabled="animationEnabled"
      active-class="possibilities-section__observer"
      :once="true"
      once-class="possibilities-section__observer--visible"
      :threshold="isDesktop ? 0.4 : 0.3"
    >
      <BackgroundGradient class="possibilities-section__background">
        <div class="possibilities-section__header">
          <h3 class="possibilities-section__title" v-html="getTitleBlock('promo', 'possibilities', 'title')"></h3>
          <p class="possibilities-section__subtitle" v-html="getTitleBlock('promo', 'possibilities', 'description')"></p>
        </div>
        <Arrows class="possibilities-section__arrows" />
        <PossibilitiesList :animation-enabled="animationEnabled" />
      </BackgroundGradient>
      <div class="possibilities-section__footer"></div>
    </IntersectionObserver>
  </div>
</template>

<script>
import clsx from 'clsx';
import { mapGetters } from 'vuex';
import Arrows from './arrows';
import BackgroundGradient from './background-gradient';
import PossibilitiesList from './possibilities-list';
import { textBlock } from '@/entities/text-block';
import { IntersectionObserver } from '@/shared/ui/intersection-observer';

export default {
  props: {
    animationEnabled: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [ textBlock ],
  components: { BackgroundGradient, Arrows, IntersectionObserver, PossibilitiesList },
  computed: {
    ...mapGetters('layoutModule', ['isDesktop']),
  },
  methods: { clsx },
};
</script>

<style lang="scss" scoped>
.possibilities-section {
  position: relative;
  height: 840px;

  @media (max-width: 1050px) {
    height: 880px;
  }

  @media (max-width: 720px) {
    height: 1220px;
  }

  @media (max-width: 500px) {
    height: 1180px;
  }

  &__background {
    clip-path: inset(0 0 100% 0);
    animation: possibilities-background-animation 1.5s linear forwards;
    animation-play-state: paused;
  }

  &__header {
    position: relative;
  }

  &__title {
    position: absolute;
    top: 72px;
    left: 0;
    right: 0;
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 52px;
    font-weight: 500;
    color: #FFFFFF;
    text-align: center;
    transform: translateY(100%);
    opacity: 0;
    animation: slideup-text-animation 0.5s linear forwards;
    animation-delay: 1.5s;
    animation-play-state: paused;

    @media (max-width: 620px) {
      font-size: 44px;
    }

    @media (max-width: 520px) {
      font-size: 32px;
    }
  }

  &__subtitle {
    position: absolute;
    top: 215px;
    left: 0;
    right: 0;
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #EBF2FF;
    text-align: center;
    transform: translateY(100%);
    opacity: 0;
    animation: slideup-text-animation 0.5s linear forwards;
    animation-delay: 2s;
    animation-play-state: paused;

    @media (max-width: 600px) {
      top: 200px;
    }

    @media (max-width: 500px) {
      top: 160px;
    }

    @media (max-width: 375px) {
      top: 190px;
    }
  }

  &__arrows {
    position: absolute;
    top: 260px;
    left: 50%;
    transform: translateX(-50%);
    clip-path: inset(0 0 100% 0);
    animation: arrows-animation 0.5s linear forwards;
    animation-delay: 2.5s;
    animation-play-state: paused;

    @media (max-width: 600px) {
      top: 245px;
    }

    @media (max-width: 500px) {
      top: 205px;
    }

    @media (max-width: 375px) {
      top: 225px;
    }
  }

  &__footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 15px;
    background: linear-gradient(to bottom, rgba(202, 221, 252, 0) 0%, #FCFDFF 100%);
  }

  &__observer--visible {
    .possibilities-section__background,
    .possibilities-section__title,
    .possibilities-section__subtitle,
    .possibilities-section__arrows {
      animation-play-state: running;
    }

    ::v-deep .possibilities-list__item {
      &--analytics, &--team, &--tools, &--calendar, &--propositions, &--control {
        animation-play-state: running;
      }
    }
  }
}

.animations-disabled,
.animations-disabled * {
  animation: none !important;
  animation-duration: 0s !important;
  animation-delay: 0s !important;
  transition: none !important;
}

.animations-disabled {
  .possibilities-section {
    &__background {
      clip-path: inset(0 0 0 0);
    }

    &__title {
      opacity: 1;
      clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
      transform: translateY(0);
    }

    &__subtitle {
      opacity: 1;
      clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
      transform: translateY(0);
    }

    &__arrows {
      clip-path: inset(0 0 0 0);
    }
  }
}

@keyframes possibilities-background-animation {
  from {
    clip-path: inset(0 0 100% 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}

@keyframes slideup-text-animation {
  0% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 0%, 0 0%);
    transform: translateY(100%);
  }
  20% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
    transform: translateY(80%);
  }
  40% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 40%, 0 40%);
    transform: translateY(60%);
  }
  60% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
    transform: translateY(40%);
  }
  80% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 80%, 0 80%);
    transform: translateY(20%);
  }
  100% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    transform: translateY(0);
  }
}

@keyframes arrows-animation {
  from {
    clip-path: inset(0 0 100% 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}
</style>
